import { useState, useRef, useEffect } from 'react';
import { Button, Checkbox, Input, Radio, Space } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { InvertFillButton } from '../InvertFillButton';
import './dropdown.scss';

interface DropdownProps {
  filterName: string;
  filterKey: string;
  checkBoxOptions: Array<string | { name?: string; label?: string; value?: string }>;
  onSubmitFilter: (filterKey: string, list: any) => void;
  width?: any;
  modalWidth?: any;
  selectedValue?: any;
  allowMultiple?: boolean;
  style?: React.CSSProperties;
}

const CheckboxGroup = Checkbox.Group;

const Dropdown = (props: DropdownProps) => {
  const {
    checkBoxOptions,
    filterName,
    filterKey,
    onSubmitFilter,
    allowMultiple = false,
    style,
  } = props;

  const normalizedOptions = checkBoxOptions.map(opt => {
    if (typeof opt === 'string') {
      return { label: opt, value: opt };
    }
    const value = opt.value || opt.name || opt.label || '';
    const label = opt.name || opt.label || opt.value || '';
    return { label, value };
  });
  const valueToLabel = normalizedOptions.reduce((acc: Record<string, string>, opt) => {
    acc[String(opt.value)] = String(opt.label);
    return acc;
  }, {});

  const [selectedValue, setSelectedValue] = useState<any>(
    props.selectedValue || (allowMultiple ? [] : undefined)
  );
  const [show, setShow] = useState(false);
  const [optionSearch, setOptionSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const visibleOptions = normalizedOptions.filter(option => {
    const searchText = optionSearch.trim().toLowerCase();
    return !searchText || `${option.label} ${option.value}`.toLowerCase().includes(searchText);
  });

  useEffect(() => {
    setSelectedValue(props.selectedValue || (allowMultiple ? [] : undefined));
  }, [props.selectedValue, allowMultiple]);

  const onClickSetShow = () => {
    setShow(show => {
      if (show) setOptionSearch('');
      return !show;
    });
  };

  const onSelect = (value: any) => {
    setSelectedValue(value);
    if (!allowMultiple) {
      // For single select, we can auto-submit or just let the user click Apply
      // Auto-submit provides a smoother UX for single select
      onSubmitFilter(filterKey, value);
      setShow(false);
    }
  };

  const clearAllChecked = () => {
    const emptyValue = allowMultiple ? [] : undefined;
    setSelectedValue(emptyValue);
    onSubmitFilter(filterKey, emptyValue);
    setOptionSearch('');
    setShow(false);
  };

  const onSubmit = () => {
    onSubmitFilter(filterKey, selectedValue);
    setOptionSearch('');
    setShow(false);
  };

  useEffect(() => {
    const handleClickOutSide = (e: MouseEvent) => {
      if (show && ref.current && !ref.current.contains(e.target as Node)) {
        setShow(false);
        setOptionSearch('');
        onSubmitFilter(filterKey, selectedValue);
      }
    };
    document.addEventListener('click', handleClickOutSide);
    return () => {
      document.removeEventListener('click', handleClickOutSide);
    };
  }, [show, filterKey, onSubmitFilter, selectedValue]);

  const getActiveCount = () => {
    if (allowMultiple) return selectedValue?.length || 0;
    return selectedValue ? 1 : 0;
  };

  const getSelectedLabel = () => {
    if (allowMultiple) {
      const values = Array.isArray(selectedValue) ? selectedValue.filter(Boolean) : [];
      if (values.length === 0) return filterName;
      const firstLabel = valueToLabel[String(values[0])] || String(values[0]);
      if (values.length === 1) return firstLabel;
      return `${firstLabel} (+${values.length - 1})`;
    }

    if (!selectedValue) return filterName;
    return valueToLabel[String(selectedValue)] || String(selectedValue);
  };

  return (
    <div ref={ref} className="dropdown-wrapper" style={{ position: 'relative' }}>
      <Button
        style={{
          width: props.width ? props.width : 'auto',
          minWidth: '160px',
          height: '40px',
          borderRadius: '8px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          ...style,
        }}
        className={`dropdown-btn ${getActiveCount() > 0 ? 'active-filter' : ''}`}
        onClick={onClickSetShow}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: 1 }}>
          <span
            style={{
              maxWidth: '320px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: style?.color || (getActiveCount() > 0 ? '#004195' : 'inherit'),
            }}
          >
            {getSelectedLabel()}
          </span>
          {allowMultiple && getActiveCount() > 0 && (
            <span
              style={{
                background: '#0b53d9',
                color: 'white',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
              }}
            >
              {getActiveCount()}
            </span>
          )}
        </div>
        <DownOutlined
          style={{
            fontSize: '12px',
            color: getActiveCount() > 0 ? '#004195' : '#64748b',
            marginLeft: '15px',
          }}
        />
      </Button>

      {show && (
        <div
          className="checkbox-wrapper"
          style={{
            width: props.modalWidth ? props.modalWidth : '300px',
            position: 'absolute',
            top: '45px',
            left: 0,
            zIndex: 1000,
            background: 'white',
            padding: '12px',
            borderRadius: '8px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            border: '1px solid #e2e8f0',
          }}
        >
          <Input
            allowClear
            value={optionSearch}
            placeholder={`Search ${filterName}`}
            onChange={e => setOptionSearch(e.target.value)}
            style={{ marginBottom: '12px', borderRadius: '8px' }}
          />
          <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '12px' }}>
            {allowMultiple ? (
              <CheckboxGroup
                options={visibleOptions}
                value={selectedValue}
                onChange={onSelect}
                style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
              />
            ) : (
              <Radio.Group
                value={selectedValue}
                onChange={e => onSelect(e.target.value)}
                style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}
              >
                {visibleOptions.map(opt => (
                  <Radio key={String(opt.value)} value={opt.value}>
                    {opt.label}
                  </Radio>
                ))}
              </Radio.Group>
            )}
          </div>
          <div
            className="dropdown-footer"
            style={{
              display: 'flex',
              gap: '8px',
              borderTop: '1px solid #f1f5f9',
              paddingTop: '12px',
            }}
          >
            <InvertFillButton
              variant="white"
              size="sm"
              fontSize="lg"
              onClick={clearAllChecked}
              style={{ flex: 1 }}
            >
              Clear
            </InvertFillButton>
            {allowMultiple && (
              <InvertFillButton
                variant="blue"
                size="sm"
                fontSize="lg"
                onClick={onSubmit}
                style={{ flex: 1 }}
              >
                Apply
              </InvertFillButton>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dropdown;
